"use client";
import {
  EmptyPageData,
  FavoritesPageData,
  TranslationData,
  WishlistItem,
  WishlistResponse,
  ProductAttributeOverride,
} from "@/common/lib/types";
import FavoritesItemCard from "./FavoritesItemsCard";
import { useEffect, useState } from "react";
import { hasArrayElements, mapEdgesToItems } from "@/apiUtils/commonUtils";
import { PageInfo } from "@/api/saleor/generated/graphql";
import { getWishlist } from "@/api/saleor/services/wishlist";
import Button from "@/components/ui/button/Button";
import { automationConstants } from "@/apiUtils/automationUtils";
import FavoritesViewSkeleton from "./FavoritesViewSkeleton";
import HeaderVariants from "@/components/common/headerVariants/HeaderVariants";
import { CLink } from "@/components/common/customLink/CustomLink";

interface IProps {
  favoritesData: FavoritesPageData;
  translations: TranslationData;
  wishlistItems: WishlistItem[];
  isGuest?: boolean;
  emptyPage?: EmptyPageData;
  isWishlistEmpty?: boolean;
  isVtoEnabledLocation?: boolean;
  wishlistId?: string;
  pageInfo?: PageInfo;
  productAttributeOverrides?: ProductAttributeOverride[];
}
const FavoriteItems = ({
  favoritesData,
  wishlistItems,
  isGuest,
  translations,
  emptyPage,
  wishlistId,
  isWishlistEmpty,
  pageInfo,
  isVtoEnabledLocation,
  productAttributeOverrides,
}: IProps) => {
  const PAGE_SIZE = 10;

  const [items, setItems] = useState<WishlistItem[]>(wishlistItems);
  const [pageData, setPageData] = useState<PageInfo | undefined>(pageInfo);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleGetMoreData = () => {
    setIsLoading(true);
    if (wishlistId) {
      getWishlist(wishlistId, PAGE_SIZE, pageData?.endCursor ?? "")
        .then((response) => {
          setIsLoading(false);
          if (response) {
            const newWishlistItems: WishlistItem[] = mapEdgesToItems(response?.items);
            setItems([...items, ...newWishlistItems]);
            setPageData(response?.items?.pageInfo);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };
  const removeProductFromLocalWishlist = (wishlistResponse: WishlistResponse) => {
    const newWishlistItems: WishlistItem[] = mapEdgesToItems(wishlistResponse?.items);
    setItems(newWishlistItems);
    setPageData(wishlistResponse?.items?.pageInfo);
  };

  return (
    <>
      {hasArrayElements(items) ? (
        !isLoading ? (
          isGuest ? (
            <>
              <div className="flex justify-center bg-tertiary-7 p-4 md:p-8">
                <div className="flex w-[881px] flex-col gap-6">
                  {items?.map((item, idx) => (
                    <FavoritesItemCard
                      key={item?.variant?.id}
                      favoritesData={favoritesData}
                      item={item}
                      automationPayload={[automationConstants.WISHLIST]}
                      wishlistId={wishlistId}
                      translations={translations}
                      removeProductFromLocalWishlist={removeProductFromLocalWishlist}
                      isVtoEnabledLocation={isVtoEnabledLocation}
                      index={idx}
                      productAttributeOverrides={productAttributeOverrides}
                    />
                  ))}
                </div>
              </div>
              {pageData?.hasNextPage ? (
                <div className="mt-8 flex justify-center">
                  <Button onClick={() => handleGetMoreData()}>
                    {translations?.favorites?.favorites_load_more_button}
                  </Button>
                </div>
              ) : (
                ""
              )}
            </>
          ) : (
            <>
              <div className="flex w-full max-w-[913px] justify-center bg-tertiary-7 p-4 md:p-8">
                <div className="flex w-full max-w-[881px] flex-col gap-6">
                  {items?.map((item, idx) => (
                    <FavoritesItemCard
                      key={item?.variant?.id}
                      favoritesData={favoritesData}
                      item={item}
                      automationPayload={[automationConstants.MY_ACCOUNT_WISHLIST]}
                      wishlistId={wishlistId}
                      translations={translations}
                      removeProductFromLocalWishlist={removeProductFromLocalWishlist}
                      productAttributeOverrides={productAttributeOverrides}
                      // isVtoEnabledLocation={isVtoEnabledLocation}
                    />
                  ))}
                </div>
              </div>
              {pageData?.hasNextPage ? (
                <div className="\\ mt-8 flex w-full justify-center">
                  <Button onClick={() => handleGetMoreData()}>
                    {translations?.favorites?.favorites_load_more_button}
                  </Button>
                </div>
              ) : (
                ""
              )}
            </>
          )
        ) : isGuest ? (
          <div>
            <FavoritesViewSkeleton />
          </div>
        ) : (
          <div className="mt-40">
            <FavoritesViewSkeleton />
          </div>
        )
      ) : (
        <div className={isGuest ? "container flex min-h-[60vh] w-full flex-col items-center justify-center py-20" : "flex flex-col items-center gap-2 py-4 text-center"}>
          <p className="text-xl font-medium md:text-[22px]">{emptyPage?.title}</p>
          <div
            className="m-auto text-center text-lg text-tertiary-6"
            dangerouslySetInnerHTML={{ __html: emptyPage?.description as unknown as TrustedHTML }}
          ></div>
          {emptyPage?.button && (
            <CLink
              href={emptyPage.button.link?.url || "/"}
              className="mt-8 border-2 border-black px-4 py-2"
            >
              {emptyPage.button.title || ""}
            </CLink>
          )}
          {emptyPage?.editsSection && isGuest && (
            <>
              <h4 className="mb-8 mt-16 text-xl font-medium md:text-[22px]">
                {emptyPage.editsSection.title || ""}
              </h4>
              <HeaderVariants
                variant="withImageGrid"
                images={
                  (emptyPage.editsSection.edits || []).map((item) => ({
                    src: item?.image || "",
                    alt: item?.title || "",
                    label: item?.title || "",
                    url: item?.link?.url || "",
                  }))
                }
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default FavoriteItems;
